import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  Briefcase, 
  ArrowLeft, 
  MessageSquare, 
  TrendingUp, 
  TrendingDown,
  Globe,
  Tag,
  Eye,
  EyeOff,
  Copy,
  CheckCircle,
  ExternalLink
} from 'lucide-react';
import { apiService, PortfolioDetail as PortfolioDetailType } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const PortfolioDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [portfolioDetail, setPortfolioDetail] = useState<PortfolioDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showKeywords, setShowKeywords] = useState(false);
  const [showTags, setShowTags] = useState(false);

  const fetchPortfolioDetail = useCallback(async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getPortfolioDetail(parseInt(id));
      setPortfolioDetail(response.data);
    } catch (err) {
      setError('Failed to load portfolio details. Please try again.');
      console.error('Error fetching portfolio detail:', err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPortfolioDetail();
  }, [fetchPortfolioDetail, id]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getSentimentColor = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return 'text-green-600 bg-green-100';
      case 'negative':
        return 'text-red-600 bg-red-100';
      case 'neutral':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getSentimentIcon = (sentiment: string) => {
    switch (sentiment.toLowerCase()) {
      case 'positive':
        return <TrendingUp className="h-4 w-4" />;
      case 'negative':
        return <TrendingDown className="h-4 w-4" />;
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <LoadingSpinner size="lg" text="Loading portfolio details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <ErrorMessage message={error} onRetry={fetchPortfolioDetail} />
      </div>
    );
  }

  if (!portfolioDetail) {
    return (
      <div className="space-y-6">
        <ErrorMessage message="Portfolio not found" />
      </div>
    );
  }

  const { portfolio, phrase_analysis, domains } = portfolioDetail;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Link
            to="/dashboard/portfolios"
            className="flex items-center space-x-2 text-blue-600 hover:text-blue-700 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Portfolios</span>
          </Link>
          <div className="h-6 w-px bg-gray-300"></div>
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{portfolio.name}</h1>
              <p className="text-gray-600">Portfolio Details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Portfolio Information */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-2 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Portfolio Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Description</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{portfolio.description}</p>
            </div>
            <div>
              <h3 className="font-medium text-gray-900 mb-2">Details</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Created:</span>
                  <span className="font-medium">{formatDate(portfolio.created_at)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Keywords:</span>
                  <span className="font-medium">{portfolio.keywords_explosion.length}</span>
                  <span className="text-gray-500">Domains:</span>
                  <span className="font-medium">{domains.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Keywords Section */}
        <div className="p-2 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Keywords</h3>
            <button
              onClick={() => setShowKeywords(!showKeywords)}
              className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
            >
              {showKeywords ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              <span>{showKeywords ? 'Hide' : 'Show'} keywords</span>
            </button>
          </div>
          
          {showKeywords && (
            <div className="max-h-60 overflow-y-auto">
              <div className="flex flex-wrap gap-2">
                {Array.isArray(portfolio.keywords_explosion) && portfolio.keywords_explosion.map((keyword, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                  >
                    {keyword}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Phrase Analysis */}
        <div className="p-2 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Phrase Analysis</h3>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <h4 className="font-medium text-gray-900 mb-2">Original Phrase</h4>
            <p className="text-gray-700 text-sm italic mb-4">"{phrase_analysis.phrase}"</p>
            
            <div className="flex items-center space-x-4 mb-4">
              <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${getSentimentColor(phrase_analysis.sentiment)}`}>
                {getSentimentIcon(phrase_analysis.sentiment)}
                <span className="capitalize">{phrase_analysis.sentiment}</span>
              </div>
              <div className="text-sm text-gray-500">
                Created: {formatDate(phrase_analysis.created_at)}
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div className="text-center">
                <div className="text-green-600 font-semibold">
                  {(phrase_analysis.pos * 100).toFixed(1)}%
                </div>
                <div className="text-gray-500">Positive</div>
              </div>
              <div className="text-center">
                <div className="text-red-600 font-semibold">
                  {(phrase_analysis.neg * 100).toFixed(1)}%
                </div>
                <div className="text-gray-500">Negative</div>
              </div>
              <div className="text-center">
                <div className="text-gray-600 font-semibold">
                  {(phrase_analysis.neu * 100).toFixed(1)}%
                </div>
                <div className="text-gray-500">Neutral</div>
              </div>
            </div>
          </div>

          {/* Tags Section */}
          {Array.isArray(phrase_analysis.tags) && phrase_analysis.tags.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-medium text-gray-900 flex items-center">
                  <Tag className="h-4 w-4 mr-2" />
                  Semantic Tags
                </h4>
                <button
                  onClick={() => setShowTags(!showTags)}
                  className="flex items-center space-x-1 text-blue-600 text-sm font-medium hover:text-blue-700"
                >
                  {showTags ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  <span>{showTags ? 'Hide' : 'Show'} tags</span>
                </button>
              </div>
              
              {showTags && (
                <div className="space-y-3">
                  {phrase_analysis.tags.map((tag, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <h5 className="font-medium text-gray-900 mb-2">{tag.tagCategory}</h5>
                      <div className="flex flex-wrap gap-2">
                        {tag.associatedTerms.map((term, termIndex) => (
                          <span
                            key={termIndex}
                            className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
                          >
                            {term}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Generated Domains */}
        <div className="p-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Globe className="h-5 w-5 mr-2" />
            Generated Domains ({domains.length})
          </h3>
          {domains.length === 0 ? (
            <div className="text-center py-8">
              <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No domains generated</h4>
              <p className="text-gray-600">No domains have been generated for this portfolio yet.</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Domain
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Source
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Purchase Price
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Renewal Price
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Transfer Price
                      </th>
                      <th className="px-2 py-1 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {domains.map((domain, index) => (
                      <tr key={domain.id || index} className="hover:bg-gray-50">
                        <td className="px-2 py-1 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0">
                              <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                                <Globe className="h-4 w-4 text-green-600" />
                              </div>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {domain.domain_name}
                              </div>
                              <div className="text-sm text-gray-500">
                                TLD: {domain.tld}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-2 py-1">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {domain.description}
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {domain.source}
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {domain.purchase_price}
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {domain.renewal_price}
                          </div>
                        </td>

                        <td className="px-2 py-1">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {domain.transfer_price}
                          </div>
                        </td>

                        <td className="px-2 py-1 whitespace-nowrap">
                          <DomainActions domain={domain} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PortfolioDetail; 

function DomainActions({ domain }: { domain: any }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(domain.domain_name);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // ignore
    }
  };
  return (
    <div className="flex space-x-2">
      <button
        onClick={handleCopy}
        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        title="Copy domain"
      >
        {copied ? (
          <CheckCircle className="h-4 w-4 text-green-500" />
        ) : (
          <Copy className="h-4 w-4 text-gray-600" />
        )}
      </button>
      <button
        onClick={() => window.open(`/dashboard/domains/${domain.domain_name}`, '_blank')}
        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        title="View domain details"
      >
        <Eye className="h-4 w-4 text-gray-600" />
      </button>
      <button
        onClick={() => window.open(`https://${domain.domain_name}`, '_blank')}
        className="p-2 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
        title="Visit domain website"
      >
        <ExternalLink className="h-4 w-4 text-gray-600" />
      </button>
    </div>
  );
} 